
import { useState } from 'react';
import MarkdownBlock from '../LazyMarkdownBlock';
import KnowledgeReferences from './KnowledgeReferences';
import { resolveKnowledgeLinks } from '../../lib/utils';
import { isNormalFinishReason, isTokenLimitFinishReason } from '../../lib/llm/api';

export default function ChatMessage({
  message,
  concepts,
  notes,
  insights,
  debugMode,
  handleResend,
  onReferenceClick,
  openMemoModal,
  isSummarizing,
  openSettings,
}) {
  const source = message.source ?? message.role;
  const isAssistant = source === 'assistant' || source === 'buddy';
  const isError = message.kind === 'error';
  const finishReason = message.metadata?.finishReason;
  const isTokenLimitFinish = isTokenLimitFinishReason(finishReason);
  const shouldShowFinishReason = isAssistant && !isNormalFinishReason(finishReason);
  const [showDebug, setShowDebug] = useState(false);

  if (isError) {
    const isApiKeyMissing = (message.body ?? message.text)?.includes('[API_KEY_MISSING]');
    const displayMessage = isApiKeyMissing
      ? (message.body ?? message.text).replace('[API_KEY_MISSING] ', '')
      : (message.body ?? message.text);

    return (
      <div className="flex justify-start">
        <div className="bg-rose-950/30 border border-rose-500/50 p-3 rounded-2xl rounded-bl-sm max-w-[92%] shadow-lg">
          <div className="flex items-center gap-2 text-rose-300 font-bold text-sm mb-1">
            <span>⚠</span>
            <span>{displayMessage}</span>
          </div>

          <div className="flex items-center gap-2 mt-3">
            {isApiKeyMissing && openSettings && (
              <button
                onClick={() => openSettings()}
                className="bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold px-3 py-1 rounded-full transition-colors active:scale-95"
              >
                Settingsを開く
              </button>
            )}
            {handleResend && message.metadata?.retryData && !isApiKeyMissing && (
              <button
                onClick={() => handleResend(message)}
                className="bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold px-3 py-1 rounded-full transition-colors active:scale-95"
              >
                再送信
              </button>
            )}
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="text-rose-400/70 hover:text-rose-300 text-[10px] font-medium underline underline-offset-2"
            >
              {showDebug ? '詳細を隠す' : '詳細を表示'}
            </button>
          </div>

          {(showDebug || debugMode) && (
            <div className="mt-3 pt-3 border-t border-rose-500/20 text-[10px] font-mono text-rose-300/80 space-y-1 overflow-x-auto">
              <div className="flex gap-2"><span className="opacity-50 w-20 shrink-0 uppercase tracking-tighter">provider:</span> {message.metadata?.provider || 'unknown'}</div>
              <div className="flex gap-2"><span className="opacity-50 w-20 shrink-0 uppercase tracking-tighter">model:</span> {message.metadata?.model || 'unknown'}</div>
              <div className="flex gap-2"><span className="opacity-50 w-20 shrink-0 uppercase tracking-tighter">finishReason:</span> {message.metadata?.finishReason || 'unknown'}</div>
              <div className="mt-2 opacity-50 uppercase tracking-tighter">error detail:</div>
              <div className="bg-rose-950/50 p-1.5 rounded border border-rose-500/10 whitespace-pre-wrap break-all mt-1">
                {message.metadata?.error || 'No error message'}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}>
      <div className={`${
        isAssistant 
          ? message.mode === 'anti'
            ? 'bg-rose-950/60 border border-rose-800/50 rounded-bl-sm'
            : 'bg-gray-800 border border-gray-700 rounded-bl-sm'
          : 'bg-indigo-600 text-white rounded-br-sm'
      } p-2.5 rounded-2xl max-w-[92%] text-[13px] leading-relaxed shadow-sm`}>
        <MarkdownBlock text={isAssistant ? resolveKnowledgeLinks(message.body ?? message.text, { concepts, notes, insights }) : (message.body ?? message.text)} />
        {shouldShowFinishReason && (
          <div className="mt-2 space-y-1">
            {isTokenLimitFinish && (
              <div role="alert" className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] text-amber-100 bg-amber-950/60 border border-amber-700/40 font-medium">
                返答が途中で切れた可能性があります
              </div>
            )}
            <div className="text-[10px] text-gray-500 font-mono">
              finishReason: {finishReason}
            </div>
          </div>
        )}
        {isAssistant && (
          <div className="mt-2 pt-2 border-t border-gray-700/30">
            {message.metadata?.referencedAssetIds?.length > 0 || message.metadata?.referencedNoteIds?.length > 0 || message.metadata?.referencedInsightIds?.length > 0 ? (
              <KnowledgeReferences
                metadata={message.metadata}
                concepts={concepts}
                notes={notes}
                insights={insights}
                onReferenceClick={onReferenceClick}
              />
            ) : (
              <div className="text-[9px] text-gray-600 font-medium italic">現在の会話のみ</div>
            )}
            {message.kind === 'memo_suggestion' && openMemoModal && (
              <button onClick={() => openMemoModal()} disabled={isSummarizing} className="mt-3 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all active:scale-95 flex items-center gap-1.5">
                {isSummarizing && (
                  <svg className="animate-spin h-3 w-3 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isSummarizing ? 'Noteまとめ中' : 'Noteまとめ'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
